/**
 * Storage access that never throws, with a transparent in-memory fallback.
 *
 * Some browser environments make `window.localStorage` / `window.sessionStorage`
 * unusable in ways that crash naive access:
 *   - the global is `null` (Firefox with `dom.storage.enabled = false`, and some
 *     "block all storage" privacy settings) — `localStorage.getItem(...)` throws
 *     "Cannot read properties of null (reading 'getItem')" (Sentry TSG-UI-1G);
 *   - reading the global itself throws a `SecurityError` (sandboxed iframes,
 *     some strict tracking-prevention modes);
 *   - the object exists but `setItem` throws (Safari Private Mode historically,
 *     or `QuotaExceededError` when storage is full).
 *
 * `safeStorage` resolves the real `Storage` inside a try/catch on every call, and
 * falls back to an in-memory `Map` when the real store is missing or a write is
 * rejected. This keeps the auth session working for the lifetime of the tab even
 * when persistent storage is unavailable, instead of crashing or silently
 * dropping the token. Values persist across a reload only when real storage is
 * available; otherwise they live as long as the tab, which is the best possible
 * behavior when the browser refuses to persist anything.
 */

export interface SafeStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

// Every instance registers its fallback map here so tests can reset them.
const fallbackMaps: Array<Map<string, string>> = [];

/**
 * Resolve the backing Storage, returning null (never throwing) when the global
 * is absent or accessing it raises. Re-resolved on every call so a throwing
 * global getter is caught each time instead of crashing the caller.
 */
function resolveRealStorage(getRealStorage: () => Storage): Storage | null {
  try {
    return getRealStorage() ?? null;
  } catch {
    return null;
  }
}

function createSafeStorage(getRealStorage: () => Storage): SafeStorage {
  // Holds values that could not be persisted to the real Storage (unavailable,
  // or the write was rejected). Checked first so those values stay readable
  // within the tab.
  const memory = new Map<string, string>();
  fallbackMaps.push(memory);

  return {
    getItem(key: string): string | null {
      if (memory.has(key)) {
        return memory.get(key) ?? null;
      }
      const storage = resolveRealStorage(getRealStorage);
      if (!storage) {
        return null;
      }
      try {
        return storage.getItem(key);
      } catch {
        return null;
      }
    },

    setItem(key: string, value: string): void {
      const storage = resolveRealStorage(getRealStorage);
      if (storage) {
        try {
          storage.setItem(key, value);
          // Persisted for real; drop any stale in-memory fallback for this key.
          memory.delete(key);
          return;
        } catch {
          // Write rejected (quota / private mode) — fall back to memory below so
          // the value is still readable in-tab.
        }
      }
      memory.set(key, value);
    },

    removeItem(key: string): void {
      memory.delete(key);
      const storage = resolveRealStorage(getRealStorage);
      if (!storage) {
        return;
      }
      try {
        storage.removeItem(key);
      } catch {
        // Nothing to clean up if the store is unavailable.
      }
    },
  };
}

export const safeLocalStorage = createSafeStorage(() => localStorage);
export const safeSessionStorage = createSafeStorage(() => sessionStorage);

/**
 * Test-only: clear the in-memory fallback of every safeStorage instance so
 * state does not leak between tests. Wired into the global test teardown.
 */
export function clearInMemoryStorageFallback(): void {
  for (const map of fallbackMaps) {
    map.clear();
  }
}
